/**
 * Quêtes quotidiennes et chaîne de connexion — **logique pure** (§7, LIVRAISON 2.1).
 *
 * Aujourd'hui, rien ne donne au joueur de raison de rouvrir Villopia demain plutôt que
 * dans un mois. Les quêtes donnent la raison de revenir ; la chaîne de connexion donne
 * le coût à ne pas revenir. C'est aussi ce qu'exige le **principe premier du §1** : à
 * chaque instant, un objectif atteignable sous les yeux.
 *
 * ── Trois décisions structurantes ────────────────────────────────────────────
 *
 * **Les quêtes sont DÉRIVÉES du jour, pas tirées au sort et stockées.** Le catalogue est
 * fixe et la sélection du jour se calcule à partir de la date : deux appels le même jour
 * rendent les mêmes quêtes, sans rien persister. Un tirage stocké, lui, se désynchronise
 * dès qu'une sauvegarde est restaurée sur un autre appareil — et il faudrait migrer le
 * format à chaque nouvelle quête.
 *
 * **La progression se mesure sur des COMPTEURS CUMULÉS, jamais sur des événements.**
 * Une quête « pose 5 bâtiments » compare le total du jour à celui enregistré au début du
 * jour. Compter les événements obligerait à écouter chaque action et à ne jamais en
 * manquer une ; comparer deux totaux est exact même si l'onglet s'est fermé au milieu.
 *
 * **Les récompenses sont en GEMMES, jamais en production.** Le §14 est absolu : une
 * quête qui accélérerait la production ferait du temps de jeu un avantage achetable au
 * prix d'une connexion quotidienne.
 */

import { GEM_SPENDING, MILESTONE_REWARDS } from '@/config/balance';
import { FEATURES } from '@/config/features';

/** Un jour de jeu commence à minuit **heure locale** du joueur, pas UTC. */
export function dayNumber(now: number, offsetMinutes = new Date(now).getTimezoneOffset()): number {
  return Math.floor((now - offsetMinutes * 60_000) / 86_400_000);
}

export type QuestKind = 'build' | 'earn' | 'upgrade' | 'visitors' | 'land';

export interface QuestDefinition {
  readonly id: string;
  readonly kind: QuestKind;
  /** Clé i18n : `core` ne connaît aucun texte (§4). */
  readonly labelKey: string;
  readonly target: number;
  readonly gems: number;
}

/**
 * Catalogue. Volontairement plus large que trois : la sélection quotidienne y pioche,
 * et un catalogue de la taille exacte de la sélection rendrait chaque jour identique.
 */
export const QUEST_CATALOGUE: readonly QuestDefinition[] = [
  { id: 'build-3', kind: 'build', labelKey: 'quests.build', target: 3, gems: 2 },
  { id: 'build-8', kind: 'build', labelKey: 'quests.build', target: 8, gems: 4 },
  { id: 'build-15', kind: 'build', labelKey: 'quests.build', target: 15, gems: 6 },
  { id: 'earn-small', kind: 'earn', labelKey: 'quests.earn', target: 1_000, gems: 2 },
  { id: 'earn-mid', kind: 'earn', labelKey: 'quests.earn', target: 100_000, gems: 4 },
  { id: 'earn-big', kind: 'earn', labelKey: 'quests.earn', target: 10_000_000, gems: 6 },
  { id: 'upgrade-1', kind: 'upgrade', labelKey: 'quests.upgrade', target: 1, gems: 3 },
  { id: 'upgrade-3', kind: 'upgrade', labelKey: 'quests.upgrade', target: 3, gems: 5 },
  { id: 'land-1', kind: 'land', labelKey: 'quests.land', target: 1, gems: 4 },
  { id: 'visitors-1', kind: 'visitors', labelKey: 'quests.visitors', target: 1, gems: 5 },
];

/** Nombre de quêtes proposées par jour. Trois : assez pour choisir, jamais une corvée. */
export const QUESTS_PER_DAY = 3;

/**
 * Catalogue effectivement proposable aujourd'hui.
 *
 * Une quête « reçois une visite » tirée alors que la boucle virale n'est pas en service
 * est un objectif IMPOSSIBLE — la ligne reste à 0/1 toute la journée, précisément
 * l'état que le principe premier du §1 interdit. Le filtre suit le drapeau : le jour où
 * la boucle est en production, les quêtes sociales entrent dans la rotation sans autre
 * changement. Exporté pour que les tests comparent la couverture au pool RÉEL.
 */
export function availableQuests(catalogue: readonly QuestDefinition[]): readonly QuestDefinition[] {
  return FEATURES.viralLoop ? catalogue : catalogue.filter((quest) => quest.kind !== 'visitors');
}

/**
 * Sélection déterministe d'une période : la même période rend toujours les mêmes quêtes.
 *
 * ── Le défaut que cette fonction supprime ───────────────────────────────────
 *
 * L'index se calculait `((période × parQuête + i) × pas) % taille`, avec un pas de 7 et
 * un catalogue de 9 entrées. Le facteur de période valait donc `3 × 7 = 21 ≡ 3 (mod 9)`,
 * et `pgcd(3, 9) = 3` : le multiplicateur ne prenait que **trois valeurs distinctes**.
 * Mesuré : **trois jeux de quêtes en tout, en boucle de trois jours** — alors que le
 * commentaire promettait de « parcourir toutes les entrées avant d'en répéter une ».
 *
 * ── Ce qui est fait à la place ──────────────────────────────────────────────
 *
 * On sépare les deux rôles au lieu de les multiplier l'un par l'autre :
 *
 * · **le décalage de période** avance de 11 crans par jour. 11 est premier et ne divise
 *   aucune taille de catalogue plausible (7, 8, 9, 10) : la rotation couvre donc tout le
 *   pool avant de se répéter, quel que soit l'état du drapeau des quêtes visiteurs ;
 * · **l'écart entre les trois quêtes du jour** part d'un tiers du catalogue — pour les
 *   prendre loin les unes des autres, sans quoi on tirerait « pose 3 », « pose 8 » et
 *   « pose 15 » le même jour, trois fois le même objectif.
 *
 * ── Le piège qu'un écart d'un tiers tend, et qu'il a tendu ──────────────────
 *
 * Un tiers de 9 vaut 3, qui DIVISE 9. L'ensemble `{o, o+3, o+6}` ne dépend alors que de
 * `o mod 3` : on retombait sur **trois sélections en tout**, exactement le défaut qu'on
 * venait de corriger, par un autre chemin. Le premier correctif a échoué au test qui
 * l'accompagnait — et c'est bien ce qu'on lui demandait.
 *
 * L'écart est donc le premier entier à partir de `taille / count` qui soit **premier avec
 * la taille**. Il s'ajuste tout seul si le catalogue change, ce qu'une constante écrite à
 * la main ne ferait pas.
 */
function coprimeStride(size: number, from: number): number {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  for (let stride = Math.max(1, from); stride < size + Math.max(1, from); stride++) {
    if (gcd(stride % size === 0 ? size : stride, size) === 1) return stride;
  }
  return 1;
}

function pickForPeriod(
  pool: readonly QuestDefinition[],
  period: number,
  count: number,
): readonly QuestDefinition[] {
  const size = pool.length;
  if (size === 0) return [];

  const offset = (((period * 11) % size) + size) % size;
  const spread = coprimeStride(size, Math.floor(size / count));

  const picked: QuestDefinition[] = [];
  for (let i = 0; i < count && i < size; i++) {
    const quest = pool[(offset + i * spread) % size];
    if (quest !== undefined && !picked.some((q) => q.id === quest.id)) picked.push(quest);
  }

  // Filet de sécurité si le catalogue est plus court que la sélection voulue.
  for (let i = 0; picked.length < Math.min(count, size) && i < size; i++) {
    const quest = pool[i];
    if (quest !== undefined && !picked.some((q) => q.id === quest.id)) picked.push(quest);
  }

  return picked;
}

/**
 * Quêtes du jour, éventuellement RELANCÉES (G§3, A6).
 *
 * Le décalage est un paramètre et non un tirage : la sélection doit rester déterministe,
 * sans quoi elle changerait à chaque rechargement de page. Il est donc **persisté** dans
 * `QuestState.rerolls` — une relance payée en gemmes qui se perdrait au premier F5 ne
 * serait pas rachetée une seconde fois.
 *
 * Le décalage vaut 5 crans par relance, premier avec les tailles de catalogue plausibles
 * (7 à 10) comme le pas de période vaut 11 : deux relances ne peuvent pas ramener la
 * sélection de départ. Le piège est déjà documenté sur `coprimeStride` — un pas qui
 * divise la taille du pool réduit le nombre de sélections possibles à une poignée.
 */
export function questsForDay(day: number, rerolls = 0): readonly QuestDefinition[] {
  return pickForPeriod(
    availableQuests(QUEST_CATALOGUE),
    day + Math.max(0, Math.floor(rerolls)) * 5,
    QUESTS_PER_DAY,
  );
}

/** Compteurs cumulés observés par les quêtes. Tous MONOTONES, jamais remis à zéro. */
export interface QuestCounters {
  readonly buildingsPlaced: number;
  readonly coinsEarned: number;
  readonly upgradesBought: number;
  readonly landPurchases: number;
  readonly visitorsReceived: number;
}

/** État persistant des quêtes. Trois nombres et une liste — délibérément minimal. */
export interface QuestState {
  /** Jour de la dernière remise à zéro. */
  readonly day: number;
  /** Compteurs au début du jour : la progression est la différence avec l'actuel. */
  readonly baseline: QuestCounters;
  /** Identifiants déjà encaissés aujourd'hui. */
  readonly claimed: readonly string[];
  /** Longueur de la chaîne de connexion, en jours consécutifs. */
  readonly streak: number;
  /** Jour où la récompense de chaîne a été encaissée pour la dernière fois. */
  readonly streakClaimedDay: number;
  /**
   * Relances de quêtes achetées, depuis toujours (G§3, A6).
   *
   * **Cumulatif et jamais remis à zéro** : c'est lui qui décale la sélection, et un
   * compteur qui redescendrait ramènerait le joueur sur les quêtes qu'il vient de payer
   * pour ne plus voir. Le plafond quotidien, lui, se lit sur `rerollDay`.
   */
  readonly rerolls: number;
  /** Jour de la dernière relance — porte le plafond d'une par jour. */
  readonly rerollDay: number;
  /**
   * Longueur de la chaîne DÉTRUITE par la dernière rupture, et le jour où elle l'a été.
   *
   * Sans ce souvenir, le rachat de chaîne (G§6, F1) ne pourrait rien restaurer : la
   * rupture est constatée par `rollDay`, qui écrasait la valeur d'avant. Et on ne rachète
   * que la rupture constatée LE JOUR MÊME — restaurer une chaîne rompue depuis trois jours
   * créditerait des journées que le joueur n'a pas jouées.
   */
  readonly lostStreak: number;
  readonly lostStreakDay: number;
  /** Semaine du dernier rachat de chaîne — porte le plafond hebdomadaire. */
  readonly repairedWeek: number;
}

export function createQuestState(day: number, counters: QuestCounters): QuestState {
  return {
    day,
    baseline: counters,
    claimed: [],
    streak: 1,
    streakClaimedDay: -1,
    rerolls: 0,
    rerollDay: -1,
    lostStreak: 0,
    lostStreakDay: -1,
    repairedWeek: -1,
  };
}

function counterFor(kind: QuestKind, counters: QuestCounters): number {
  switch (kind) {
    case 'build':
      return counters.buildingsPlaced;
    case 'earn':
      return counters.coinsEarned;
    case 'upgrade':
      return counters.upgradesBought;
    case 'land':
      return counters.landPurchases;
    case 'visitors':
      return counters.visitorsReceived;
  }
}

/**
 * Fait tourner la journée si nécessaire.
 *
 * La chaîne se poursuit si le joueur revient **le lendemain**, se remet à 1 s'il a sauté
 * un jour. Revenir deux fois le même jour ne la fait pas monter — sinon il suffirait de
 * recharger la page pour la gonfler.
 */
export function rollDay(state: QuestState, day: number, counters: QuestCounters): QuestState {
  /*
    Bascule MONOTONE : jamais en arrière.

    La garde ne rejetait que l'égalité, si bien que **tout** écart vidait `claimed` —
    y compris un écart négatif. Reculer l'horloge système d'un jour rouvrait les
    quêtes déjà encaissées, et osciller entre J et J+1 donnait des gemmes à l'infini.
    Le même `<=` couvre au passage les changements d'heure et les fuseaux : une
    journée de jeu ne se rejoue pas parce que le téléphone a changé de région.

    L'heure du serveur deviendra l'autorité quand la synchronisation la fournira ;
    d'ici là, la monotonie locale est la protection qui coûte le moins et couvre le
    plus.
  */
  if (day <= state.day) return state;

  const continued = day === state.day + 1;

  return {
    ...state,
    day,
    baseline: counters,
    claimed: [],
    // Un seul jour d'écart prolonge la chaîne ; au-delà, elle repart de zéro. C'est
    // ce coût qui donne son sens au fait de revenir.
    streak: continued ? state.streak + 1 : 1,
    streakClaimedDay: state.streakClaimedDay,
    /*
      La chaîne rompue est MÉMORISÉE, pas seulement effacée (G§6, F1).

      C'est ce qui rend le rachat possible sans inventer un chiffre : on restaure
      exactement ce qui a été perdu, et seulement si la rupture date de ce jour-ci. Une
      chaîne d'un seul jour ne vaut pas la peine d'être retenue — elle ne coûte rien à
      refaire, et proposer de la racheter serait une offre malhonnête.
    */
    lostStreak: continued || state.streak < 2 ? state.lostStreak : state.streak,
    lostStreakDay: continued || state.streak < 2 ? state.lostStreakDay : day,
  };
}

export interface QuestProgress {
  readonly quest: QuestDefinition;
  readonly current: number;
  readonly ratio: number;
  readonly complete: boolean;
  readonly claimed: boolean;
}

export function questProgress(
  state: QuestState,
  counters: QuestCounters,
): readonly QuestProgress[] {
  return questsForDay(state.day, state.rerolls).map((quest) => {
    // Différence avec le début du jour : exact même si l'onglet s'est fermé au milieu.
    const current = Math.max(
      0,
      counterFor(quest.kind, counters) - counterFor(quest.kind, state.baseline),
    );
    const complete = current >= quest.target;

    return {
      quest,
      current: Math.min(current, quest.target),
      ratio: quest.target <= 0 ? 1 : Math.min(1, current / quest.target),
      complete,
      claimed: state.claimed.includes(quest.id),
    };
  });
}

export interface ClaimResult {
  readonly state: QuestState;
  readonly gems: number;
  readonly ok: boolean;
}

/** Encaisse une quête terminée. Deux encaissements du même jour sont impossibles. */
export function claimQuest(
  state: QuestState,
  counters: QuestCounters,
  questId: string,
): ClaimResult {
  const entry = questProgress(state, counters).find((p) => p.quest.id === questId);
  if (entry === undefined || !entry.complete || entry.claimed) {
    return { state, gems: 0, ok: false };
  }

  return {
    state: { ...state, claimed: [...state.claimed, questId] },
    gems: entry.quest.gems,
    ok: true,
  };
}

/**
 * Récompense de chaîne de connexion, croissante sur sept jours puis stable.
 *
 * Croissante pour que rompre la chaîne au sixième jour coûte quelque chose ; plafonnée
 * pour qu'un joueur de trois cents jours ne creuse pas un écart irrattrapable — ce qui
 * découragerait tout nouveau venu.
 */
export function streakReward(streak: number): number {
  const day = Math.min(7, Math.max(1, streak));
  return MILESTONE_REWARDS.landExpansion + day;
}

export function claimStreak(state: QuestState): ClaimResult {
  if (state.streakClaimedDay === state.day) return { state, gems: 0, ok: false };

  return {
    state: { ...state, streakClaimedDay: state.day },
    gems: streakReward(state.streak),
    ok: true,
  };
}

/* ── Ce que les gemmes rachètent (G§3 A6, G§6 F1) ────────────────────────── */

/**
 * Relance les trois quêtes du jour. Une par jour — le plafond vit ici, pas dans l'écran.
 *
 * Les quêtes déjà encaissées ne se rouvrent PAS : `claimed` porte des identifiants, et
 * une quête rendue par la nouvelle sélection qui aurait déjà été payée reste payée. Sans
 * ça, relancer serait une machine à gemmes plutôt qu'un changement d'objectif.
 *
 * Une quête relancée peut arriver **déjà terminée** grâce à la progression du jour, et
 * c'est accepté : la relance coûte 10 gemmes là où la meilleure quotidienne en rapporte 6
 * — l'opération reste perdante, donc ce n'est pas une pompe, seulement un coup de chance.
 */
export function rerollDaily(state: QuestState): {
  readonly state: QuestState;
  readonly ok: boolean;
} {
  if (state.rerollDay === state.day) return { state, ok: false };
  return {
    state: { ...state, rerolls: state.rerolls + 1, rerollDay: state.day },
    ok: true,
  };
}

/** Le rachat de chaîne est-il proposable aujourd'hui ? Trois conditions, toutes vraies. */
export function canRepairStreak(state: QuestState, week: number): boolean {
  return (
    // La rupture doit avoir été constatée AUJOURD'HUI : on ne remonte pas le temps.
    state.lostStreakDay === state.day &&
    state.lostStreak >= 2 &&
    // Et une seule fois par semaine — la condition d'honnêteté de l'offre (G§6, F1).
    weeklyRepairsLeft(state, week) > 0
  );
}

/** Rachats de chaîne restants cette semaine. */
export function weeklyRepairsLeft(state: QuestState, week: number): number {
  const used = state.repairedWeek === week ? 1 : 0;
  return Math.max(0, GEM_SPENDING.streakRepair.weeklyCap - used);
}

/**
 * Rachète la chaîne rompue : elle reprend là où elle s'est cassée, plus le jour présent.
 *
 * `lostStreak + 1` et non `lostStreak` : le joueur est là aujourd'hui, ce jour compte —
 * exactement comme si la chaîne n'avait jamais été rompue. Restaurer sans le jour courant
 * ferait payer pour se retrouver un cran en arrière.
 */
export function repairStreak(
  state: QuestState,
  week: number,
): { readonly state: QuestState; readonly ok: boolean } {
  if (!canRepairStreak(state, week)) return { state, ok: false };

  return {
    ok: true,
    state: {
      ...state,
      streak: state.lostStreak + 1,
      repairedWeek: week,
      // La rupture est effacée : sans ça, le même rachat serait proposé indéfiniment.
      lostStreak: 0,
      lostStreakDay: -1,
    },
  };
}

/* ── Quêtes hebdomadaires (LIVRAISON 2.1, second horizon) ───────────────── */

/**
 * Numéro de semaine, aligné sur le LUNDI local.
 *
 * Le jour 0 de l'époque Unix est un **jeudi** : diviser bêtement par sept ferait
 * basculer la semaine un jeudi, ce qu'aucun joueur ne comprendrait.
 *
 * Le premier lundi est le jour 4 (5 janvier 1970). L'offset qui l'amène en tête de
 * période est donc **+3**, et non +4 : avec +4, la semaine courait du dimanche au
 * samedi — un décalage d'un seul jour, invisible en lecture et flagrant à l'usage.
 * C'est le test qui l'a établi, pas le raisonnement.
 */
export function weekNumber(day: number): number {
  return Math.floor((day + 3) / 7);
}

/**
 * Catalogue hebdomadaire : mêmes natures d'objectif, une tout autre échelle.
 *
 * Les cibles ne sont pas « la quotidienne × 7 » mais nettement plus ambitieuses : une
 * hebdomadaire qu'on termine le mardi ne tient personne jusqu'au dimanche. Les
 * récompenses suivent, sans quoi l'effort supplémentaire ne se justifierait pas — et
 * un test verrouille le fait que la plus petite hebdomadaire rapporte plus que la
 * meilleure quotidienne.
 */
export const WEEKLY_CATALOGUE: readonly QuestDefinition[] = [
  { id: 'w-build-40', kind: 'build', labelKey: 'quests.build', target: 40, gems: 14 },
  { id: 'w-build-80', kind: 'build', labelKey: 'quests.build', target: 80, gems: 20 },
  { id: 'w-earn-1', kind: 'earn', labelKey: 'quests.earn', target: 1e9, gems: 14 },
  { id: 'w-earn-2', kind: 'earn', labelKey: 'quests.earn', target: 1e12, gems: 20 },
  { id: 'w-upgrade-10', kind: 'upgrade', labelKey: 'quests.upgrade', target: 10, gems: 16 },
  { id: 'w-upgrade-25', kind: 'upgrade', labelKey: 'quests.upgrade', target: 25, gems: 24 },
  { id: 'w-land-3', kind: 'land', labelKey: 'quests.land', target: 3, gems: 18 },
  { id: 'w-visitors-10', kind: 'visitors', labelKey: 'quests.visitors', target: 10, gems: 25 },
];

export const WEEKLY_PER_PERIOD = 3;

/** Sélection de la semaine, déterministe — exactement le même mécanisme. */
export function questsForWeek(week: number): readonly QuestDefinition[] {
  return pickForPeriod(availableQuests(WEEKLY_CATALOGUE), week, WEEKLY_PER_PERIOD);
}

/**
 * État hebdomadaire. Même forme que le quotidien, autre période.
 *
 * Deux états distincts plutôt qu'un seul paramétré : leurs remises à zéro n'ont aucune
 * raison de coïncider, et les mêler obligerait chaque lecture à préciser laquelle.
 */
export interface WeeklyState {
  readonly week: number;
  readonly baseline: QuestCounters;
  readonly claimed: readonly string[];
}

export function createWeeklyState(week: number, counters: QuestCounters): WeeklyState {
  return { week, baseline: counters, claimed: [] };
}

export function rollWeek(state: WeeklyState, week: number, counters: QuestCounters): WeeklyState {
  // Monotone, exactement comme `rollDay` : reculer l'horloge ne rouvre rien.
  return week <= state.week ? state : { week, baseline: counters, claimed: [] };
}

export function weeklyProgress(
  state: WeeklyState,
  counters: QuestCounters,
): readonly QuestProgress[] {
  return questsForWeek(state.week).map((quest) => {
    const current = Math.max(
      0,
      counterFor(quest.kind, counters) - counterFor(quest.kind, state.baseline),
    );

    return {
      quest,
      current: Math.min(current, quest.target),
      ratio: quest.target <= 0 ? 1 : Math.min(1, current / quest.target),
      complete: current >= quest.target,
      claimed: state.claimed.includes(quest.id),
    };
  });
}

export function claimWeekly(
  state: WeeklyState,
  counters: QuestCounters,
  questId: string,
): { readonly state: WeeklyState; readonly gems: number; readonly ok: boolean } {
  const entry = weeklyProgress(state, counters).find((p) => p.quest.id === questId);
  if (entry === undefined || !entry.complete || entry.claimed) {
    return { state, gems: 0, ok: false };
  }

  return {
    state: { ...state, claimed: [...state.claimed, questId] },
    gems: entry.quest.gems,
    ok: true,
  };
}

/* ── Quand tout cela se renouvelle ──────────────────────────────────────── */

/**
 * Instant du prochain renouvellement quotidien, en millisecondes epoch.
 *
 * ══ Pourquoi cette fonction vit dans `core` ═════════════════════════════════
 *
 * Le panneau des quêtes doit dire « nouvelles quêtes dans 3 h 12 » : sans échéance
 * affichée, une quête ratée se découvre le lendemain, et une quête terminée ne donne
 * aucune raison de revenir CE SOIR plutôt que dans trois jours. C'est de l'urgence
 * douce, et c'est ce qui fait la différence entre un jeu qu'on rouvre et un jeu qu'on
 * oublie.
 *
 * Le calcul est l'INVERSE exact de `dayNumber`, et il vit à côté de lui pour cette
 * raison : deux fonctions qui définissent la même frontière de journée ne doivent pas
 * pouvoir diverger. Un « minuit » recalculé dans l'interface se serait décalé au
 * premier changement de convention — et la journée de jeu commence à minuit LOCAL, pas
 * à minuit UTC.
 */
export function nextDailyResetAt(
  now: number,
  offsetMinutes = new Date(now).getTimezoneOffset(),
): number {
  return (dayNumber(now, offsetMinutes) + 1) * 86_400_000 + offsetMinutes * 60_000;
}

/**
 * Instant du prochain renouvellement hebdomadaire, en millisecondes epoch.
 *
 * Même inversion, appliquée à `weekNumber` : la semaine bascule un LUNDI local, et
 * l'offset de +3 jours qui l'y amène est déjà justifié plus haut. Le refaire à la main
 * dans l'interface aurait ramené le décalage d'un jour que le test avait attrapé.
 */
export function nextWeeklyResetAt(
  now: number,
  offsetMinutes = new Date(now).getTimezoneOffset(),
): number {
  const day = dayNumber(now, offsetMinutes);
  const firstDayOfNextWeek = (weekNumber(day) + 1) * 7 - 3;
  return firstDayOfNextWeek * 86_400_000 + offsetMinutes * 60_000;
}
